import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"
import React, { useEffect, useState } from "react"
import { View } from "react-native"
import Animated, {
    useAnimatedProps,
    useSharedValue,
    withTiming,
    Easing,
} from "react-native-reanimated"
import Svg, { Circle } from "react-native-svg"

const AnimatedCircle = Animated.createAnimatedComponent(Circle)

/**
 * QuizScoreRing — animated circular score display for the quiz correction.
 *
 * A progress ring fills to the obtained percentage (green when passed, red
 * otherwise) while the score counts up from 0. Works on web and native
 * (react-native-svg + reanimated), unlike CustomCircularProgress which is
 * web-disabled.
 */
const QuizScoreRing = ({
    score = 0,
    total = 0,
    passed = false,
    radius = 54,
    strokeWidth = 10,
}) => {
    const pct = total > 0 ? Math.round((score / total) * 100) : 0
    const color = passed ? theme.colors.success : theme.colors.error
    const trackColor = passed
        ? theme.colors.successLight
        : theme.colors.errorLight

    const size = radius * 2 + strokeWidth
    const center = size / 2
    const circumference = 2 * Math.PI * radius

    // Ring fill animation (0 → pct)
    const progress = useSharedValue(0)
    useEffect(() => {
        progress.value = withTiming(pct, {
            duration: 900,
            easing: Easing.out(Easing.cubic),
        })
    }, [pct])

    const animatedProps = useAnimatedProps(() => ({
        strokeDashoffset: circumference * (1 - progress.value / 100),
    }))

    // Score count-up (0 → score)
    const [displayScore, setDisplayScore] = useState(0)
    useEffect(() => {
        let raf
        const start = performance.now()
        const duration = 900
        const tick = (now) => {
            const t = Math.min((now - start) / duration, 1)
            // easeOutCubic to match the ring
            const eased = 1 - Math.pow(1 - t, 3)
            setDisplayScore(Math.round(eased * score))
            if (t < 1) raf = requestAnimationFrame(tick)
        }
        raf = requestAnimationFrame(tick)
        return () => cancelAnimationFrame(raf)
    }, [score])

    return (
        <View style={{ alignItems: "center", justifyContent: "center" }}>
            <View style={{ width: size, height: size }}>
                <Svg width={size} height={size}>
                    {/* Track */}
                    <Circle
                        cx={center}
                        cy={center}
                        r={radius}
                        stroke={trackColor}
                        strokeWidth={strokeWidth}
                        fill="none"
                    />
                    {/* Progress arc — starts at top (-90°) */}
                    <AnimatedCircle
                        cx={center}
                        cy={center}
                        r={radius}
                        stroke={color}
                        strokeWidth={strokeWidth}
                        fill="none"
                        strokeLinecap="round"
                        strokeDasharray={circumference}
                        animatedProps={animatedProps}
                        transform={`rotate(-90 ${center} ${center})`}
                    />
                </Svg>

                {/* Centered score */}
                <View
                    style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <TextM fontWeight="ExtraBold" fontSize={26} color={color}>
                        {`${displayScore}/${total}`}
                    </TextM>
                    <TextM fontWeight="Bold" fontSize={13} color={color}>
                        {`${pct}%`}
                    </TextM>
                </View>
            </View>

            <TextM
                fontWeight="SemiBold"
                color={color}
                wrap
                style={{ textAlign: "center", marginTop: 10 }}
            >
                {passed ? "Quiz réussi" : "Quiz non réussi"}
            </TextM>
        </View>
    )
}

export default QuizScoreRing
